"use client";

import React, { useState, useEffect, useRef } from "react";
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Download, ListMusic, Repeat, Shuffle, AudioLines, Share2 } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { RangeInput } from "@/components/ui/range-input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
const PLAYLIST = [{
  id: "track-1",
  title: "Viaggio tra il Silenzio",
  artist: "SacroPan",
  src: "https://sacropan.it/wp-content/uploads/2026/05/viaggiotrailsilezio.mp3"
}, {
  id: "track-2",
  title: "Echi del Pasubio",
  artist: "SacroPan",
  src: "https://sacropan.it/wp-content/uploads/2026/05/echi_pasubio.mp3"
}, {
  id: "track-3",
  title: "Respiro di Roccia",
  artist: "SacroPan",
  src: "https://sacropan.it/wp-content/uploads/2026/05/respiro_roccia.mp3"
}, {
  id: "track-4",
  title: "Oltre la Linea",
  artist: "SacroPan",
  src: "https://sacropan.it/wp-content/uploads/2026/05/oltre_linea.mp3"
}];
export default function AudioPlayer() {
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTimeSec, setCurrentTimeSec] = useState(0);
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [isShuffle, setIsShuffle] = useState(false);
  const [isRepeat, setIsRepeat] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const currentTrack = PLAYLIST[currentTrackIndex];
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
      audioRef.current.muted = isMuted;
    }
  }, [volume, isMuted]);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const updateProgress = () => {
      setCurrentTimeSec(audio.currentTime);
      setProgress(audio.currentTime / audio.duration * 100);
    };
    const handleEnded = () => {
      if (isRepeat) {
        audio.currentTime = 0;
        audio.play();
      } else {
        handleNext();
      }
    };
    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("ended", handleEnded);
    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [currentTrackIndex, isRepeat, isShuffle]);
  const handlePlayPause = () => {
    if (isPlaying) {
      audioRef.current?.pause();
    } else {
      audioRef.current?.play();
    }
    setIsPlaying(!isPlaying);
  };
  const handleNext = () => {
    let nextIndex;
    if (isShuffle) {
      do {
        nextIndex = Math.floor(Math.random() * PLAYLIST.length);
      } while (nextIndex === currentTrackIndex && PLAYLIST.length > 1);
    } else {
      nextIndex = (currentTrackIndex + 1) % PLAYLIST.length;
    }
    setCurrentTrackIndex(nextIndex);
    setIsPlaying(true);
    setTimeout(() => audioRef.current?.play(), 0);
  };
  const handlePrev = () => {
    if (currentTimeSec > 3) {
      if (audioRef.current) audioRef.current.currentTime = 0;
    } else {
      const prevIndex = (currentTrackIndex - 1 + PLAYLIST.length) % PLAYLIST.length;
      setCurrentTrackIndex(prevIndex);
    }
    setIsPlaying(true);
    setTimeout(() => audioRef.current?.play(), 0);
  };
  const handleTrackSelect = (index: number) => {
    setCurrentTrackIndex(index);
    setIsPlaying(true);
    setTimeout(() => audioRef.current?.play(), 0);
  };
  const toggleMute = () => setIsMuted(!isMuted);
  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };
  return <section id="audio-player" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-lg blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 max-w-6xl relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-default font-bold mb-4 uppercase tracking-widest">Audio</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <Card className="lg:col-span-8 bg-card/40 backdrop-blur-md border-border/50 shadow-2xl overflow-hidden">
            <div className="flex flex-col md:flex-row h-full">
              <div className="w-full md:w-2/5 bg-muted/20 p-6 md:p-8 flex items-center justify-center border-b md:border-b-0 md:border-r border-border/50">
                <div className="w-full max-w-[280px] relative group">
                  <AspectRatio ratio={1}>
                    <div className="w-full h-full bg-muted flex items-center justify-center rounded-lg">
                      <AudioLines className={`size-16 text-primary/40 transition-transform duration-700 ${isPlaying ? 'scale-110' : 'scale-100'}`} />
                    </div>
                    <div className="absolute inset-0 rounded-lg bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" />
                  </AspectRatio>
                </div>
              </div>

              <audio ref={audioRef} src={currentTrack.src} className="hidden" />

              <div className="w-full md:w-3/5 p-6 md:p-8 flex flex-col justify-between gap-8">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <Badge variant="outline" className="text-primary border-primary/30 bg-primary/5">
                      {isPlaying ? "Now Playing" : "Paused"}
                    </Badge>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-primary">
                            <Share2 className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Share Track</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-default font-bold mb-1 line-clamp-1" title={currentTrack.title}>
                    {currentTrack.title}
                  </h3>
                  <p className="text-muted-foreground text-lg line-clamp-1">{currentTrack.artist}</p>
                </div>

                <div className="space-y-3">
                  <Progress value={progress} className="h-1.5 bg-muted" />
                  <div className="flex justify-between text-xs text-muted-foreground font-mono tracking-wider">
                    <span>{formatTime(currentTimeSec)}</span>
                    <span>{audioRef.current?.duration ? formatTime(audioRef.current.duration) : "--:--"}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 md:gap-2">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon" onClick={() => setIsShuffle(!isShuffle)} className={isShuffle ? "text-primary" : "text-muted-foreground"}>
                            <Shuffle className="size-4 md:size-5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Shuffle</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>

                  <div className="flex items-center gap-3 md:gap-6">
                    <Button variant="secondary" size="icon" onClick={handlePrev} className="size-10 md:size-12 rounded-lg hover:bg-primary/20 hover:text-primary transition-colors">
                      <SkipBack className="size-5" />
                    </Button>
                    <Button size="icon" onClick={handlePlayPause} className="size-14 md:size-16 rounded-lg bg-primary text-primary-foreground hover:scale-105 hover:bg-primary/90 transition-all shadow-lg shadow-primary/20">
                      {isPlaying ? <Pause className="size-6 md:size-7" /> : <Play className="size-6 md:size-7 ml-1" />}
                    </Button>
                    <Button variant="secondary" size="icon" onClick={handleNext} className="size-10 md:size-12 rounded-lg hover:bg-primary/20 hover:text-primary transition-colors">
                      <SkipForward className="size-5" />
                    </Button>
                  </div>

                  <div className="flex items-center gap-1 md:gap-2">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon" onClick={() => setIsRepeat(!isRepeat)} className={isRepeat ? "text-primary" : "text-muted-foreground"}>
                            <Repeat className="size-4 md:size-5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Repeat</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-6 border-t border-border/40">
                  <div className="flex items-center gap-3 w-1/2 max-w-[200px]">
                    <Button variant="ghost" size="icon" onClick={toggleMute} className="shrink-0 text-muted-foreground hover:text-foreground">
                      {isMuted || volume === 0 ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}
                    </Button>
                    <RangeInput value={isMuted ? 0 : volume} max={100} onValueChange={setVolume} className="[&::-webkit-slider-thumb]:bg-primary [&::-moz-range-thumb]:bg-primary" />
                  </div>
                  
                  <Button variant="outline" size="sm" className="gap-2 border-border/50 hover:bg-primary/10 hover:text-primary hover:border-primary/30">
                    <Download className="size-4" />
                    <span className="hidden sm:inline">Download</span>
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          <Card className="lg:col-span-4 flex flex-col h-[500px] lg:h-auto bg-card/40 backdrop-blur-md border-border/50 shadow-xl">
            <CardHeader className="border-b border-border/40 pb-4 bg-muted/10">
              <div className="flex items-center justify-between">
                <CardTitle className="font-default flex items-center gap-2 text-xl">
                  <ListMusic className="size-5 text-primary" />
                  Up Next
                </CardTitle>
                <Badge variant="secondary" className="text-xs font-mono">
                  {PLAYLIST.length} Tracks
                </Badge>
              </div>
            </CardHeader>
            
            <ScrollArea className="flex-1">
              <div className="p-3 space-y-1">
                {PLAYLIST.map((track, i) => {
                const isActive = i === currentTrackIndex;
                return <div data-index={i} key={track.id} onClick={() => handleTrackSelect(i)} className={`group flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all duration-200 ${isActive ? 'bg-primary/10 border border-primary/20' : 'hover:bg-accent/10 border border-transparent'}`}>
                      <div className="size-12 shrink-0 relative rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                        <div className={`absolute inset-0 bg-black/40 flex items-center justify-center transition-opacity ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                          {isActive && isPlaying ? <AudioLines className="size-5 text-primary animate-pulse" /> : <Play className="size-5 text-white ml-0.5" />}
                        </div>
                        {!isActive && <AudioLines className="size-4 text-muted-foreground/30" />}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate transition-colors ${isActive ? 'text-primary' : 'text-foreground group-hover:text-primary/80'}`}>
                          {track.title}
                        </p>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {track.artist}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
            </ScrollArea>
          </Card>
        </div>
      </div>
    </section>;
}